import { gql, useQuery } from '@apollo/client';
import {
  GraphQLEnumType,
  GraphQLEnumValue,
  GraphQLField,
  GraphQLInputField,
  GraphQLInputObjectType,
  GraphQLNamedType,
} from 'graphql/type/definition';
import moment from 'moment';
import * as monacoEditor from 'monaco-editor';
import { useMemo } from 'react';

import {
  SUPERGRAPH_SCHEMA_ID,
  SchemaInfo,
} from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { isGraphqlField } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { FieldHistogramData } from 'src/app/graph/insights/fieldPage/usage/fieldPageLatencyDistributionChart/useFieldHistogramStackInfo';
import { fieldRouteConfig } from 'src/app/graph/routes';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import {
  clientFilterDefaults,
  useFieldInsightsFilter,
} from 'src/hooks/useInsightsFilter';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { buildSchemaLeniently } from 'src/lib/buildSchemaLeniently';
import { tolerantParseSchema } from 'src/lib/chevrotain/tolerantParseSchema';
import {
  FastModeRange,
  TimeRangeOption,
} from 'src/lib/config/timeRangeOptions';
import { date, getAbsoluteDateBounds } from 'src/lib/date/date';
import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';
import {
  GetSubgraphs,
  buildSubgraphMetaHelpers,
  subgraphMetaEntitiesFragment,
  subgraphMetaSubgraphsFragment,
} from 'src/lib/graphql/subgraphMetadataHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { CoordinateKind, Resolution } from 'src/lib/graphqlTypes/types';

export type FieldType =
  | GraphQLField<unknown, unknown>
  | GraphQLInputField
  | GraphQLEnumValue;
export type OperationName = string; // query ID
export type ClientName = string;
export type LatencyPercentileChart = 'p50' | 'p90' | 'p95';
export type LatencyChartType = 'histogram' | 'heatmap' | LatencyPercentileChart;

export type PossibleRanges = ReadonlyArray<TimeRangeOption>;

export interface OperationMetrics {
  versionCount: number;
  allVersions: Set<string>;
  allOperationTypes: Set<string>;
  executions: number;
  estimatedExecutions: number;
  requests: number;
  queryId: string;
  queryName: string;
}

export interface ClientMetrics {
  requests: number;
  executions: number;
  operationMetrics: Map<OperationName, OperationMetrics>;
}

export type FieldRequest = {
  timestamp: Timestamp['output'];
  requestCount: number;
  estimatedExecutionCount: number;
};

export type FieldError = {
  timestamp: Timestamp['output'];
  errorCount: number;
  percentage: number;
};

export type FieldMetrics = {
  totalClients: number;
  totalOperations: number;
  clientMetrics: Map<ClientName, ClientMetrics>;
  requests: FieldRequest[];
  latencyDistribution: FieldHistogramData[];
  errors: FieldError[];
};

export interface FieldTimeRange {
  timeFrom: string;
  timeTo: string;
  fromDate: Date;
  toDate: Date;
  range: TimeRangeOption;
  resolution: Resolution;
  pollInterval: number;
  ranges: PossibleRanges;
  maxRangeInDays: number;
  isDefaultSelection: boolean;
  fastModeRange?: FastModeRange;
}

type SchemaInfoWithoutUrl = Omit<SchemaInfo, 'url'>;

export interface CurrentFieldAccessBounds {
  firstSeen?: Date;
  lastSeen?: Date;
  earliestRetentionTime?: Date;
  variantUsageLastReportedAt?: Date;
}

export interface CurrentFieldEmptyState {
  showEmptyState?: boolean;
  variantHasUsage?: boolean;
  fieldHasUsage?: boolean;
  fieldUsageOutsideTimeBounds?: boolean;
  fieldEarliestRetentionInDays?: number;
  fieldLastSeen?: Date;
}

export type UseCurrentSchemaCoordinateResultSuccess = {
  summaryLoading?: boolean;
  metricsLoading?: boolean;
  boundsLoading?: boolean;
  graphId: string;
  variant: string;
  fieldName: string;
  typeName: string;
  schemaInfo: SchemaInfoWithoutUrl | null;
  fieldMetrics: FieldMetrics;
  error?: undefined;
  canViewClientVersions: boolean;
  timeRange: FieldTimeRange;
  hasActiveClientFilters: boolean;
  accessBounds: CurrentFieldAccessBounds;
  getSubgraphs: GetSubgraphs | undefined;
  emptyState?: CurrentFieldEmptyState;
} & (
  | { field: FieldType; parentType: GraphQLNamedType; isRootType: boolean }
  | { field: undefined; parentType: undefined; isRootType: boolean }
);

export type UseCurrentSchemaCoordinateResultError = { error: Error };

export function isCurrentFieldResultError(
  result:
    | UseCurrentSchemaCoordinateResultSuccess
    | UseCurrentSchemaCoordinateResultError,
): result is UseCurrentSchemaCoordinateResultError {
  return !!result.error;
}

const ServiceFieldUsageRecordFragment = gql`
  fragment ServiceFieldUsageRecordFragment on ServiceFieldUsageRecord {
    groupBy {
      clientName
      clientVersion
      operationType
      queryName
      queryId
    }
    metrics {
      executionCount
      estimatedExecutionCount
      referencingOperationCount
    }
  }
`;

const ServiceCoordinateUsageRecordFragment = gql`
  fragment ServiceCoordinateUsageRecordFragment on ServiceCoordinateUsageRecord {
    groupBy {
      clientName
      clientVersion
      operationType
      queryName
      queryId
    }
    metrics {
      referencingOperationCount
    }
  }
`;

const DEFAULT_RANGE: TimeRangeOption = 'lastDay';

export const useCurrentSchemaCoordinate = ():
  | UseCurrentSchemaCoordinateResultSuccess
  | UseCurrentSchemaCoordinateResultError => {
  const { routeParams: filterParams, hasActiveFiltersForSubset } =
    useFieldInsightsFilter();
  const {
    graphId,
    graphVariant = '',
    fieldName,
    typeName,
    coordinateKind = CoordinateKind.OBJECT_FIELD,
  } = useRouteParams(fieldRouteConfig);

  const [currentAccountId] = useCurrentAccountId();
  const { currentPlan } = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  function findReasonableResolutionCharts(diff: number): Resolution {
    if (diff < moment.duration(7, 'days').asSeconds()) {
      return Resolution.R1H;
    }
    if (diff < moment.duration(1, 'month').asSeconds()) {
      return Resolution.R6H;
    }
    return Resolution.R1D;
  }

  const partialTimeRange = useMemo<
    Omit<
      FieldTimeRange,
      'ranges' | 'maxRangeInDays' | 'onRangeSelected' | 'resetTimeRange'
    >
  >(() => {
    const { from, to, range = DEFAULT_RANGE, fastModeRange } = filterParams;

    const { timeFrom: fromDate, timeTo: toDate } = getAbsoluteDateBounds(
      range,
      { from, to },
    );
    const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
      range,
      { from, to },
      findReasonableResolutionCharts,
      fastModeRange,
    );
    const isDefaultSelection = !from && !to && range === DEFAULT_RANGE;
    return {
      timeFrom,
      timeTo,
      range,
      resolution,
      pollInterval,
      fromDate,
      toDate,
      isDefaultSelection,
      fastModeRange,
    };
  }, [filterParams]);

  const fieldUsageFilter: GraphQLTypes.ServiceFieldUsageFilter = {
    parentType: typeName,
    fieldName,
    schemaTag: graphVariant,
    // leaving this out until we support filtering on all stats
    // or: convertClientFilterToStatsFilter(filterParams.clients),
  };
  const fieldExecutionsFilter: GraphQLTypes.ServiceFieldExecutionsFilter = {
    parentType: typeName,
    fieldName,
    schemaTag: graphVariant,
  };
  const coordinateUsageFilter: GraphQLTypes.ServiceCoordinateUsageFilter = {
    kind: coordinateKind,
    namedType: typeName,
    namedAttribute: fieldName,
    schemaTag: graphVariant,
    // leaving this out until we support filtering on all stats
    // or: convertClientFilterToStatsFilter(filterParams.clients),
  };

  const {
    data: schemaData,
    loading: schemaLoading,
    error: schemaError,
  } = useQuery<
    GraphQLTypes.CoordinateLandingPageSchemaQuery,
    GraphQLTypes.CoordinateLandingPageSchemaQueryVariables
  >(
    gql`
      query CoordinateLandingPageSchemaQuery(
        $graphId: ID!
        $graphVariant: String!
      ) {
        service(id: $graphId) {
          id
          schema(tag: $graphVariant) {
            document
            createdAt
            hash
            gitContext {
              commit
            }
            fieldCount
            typeCount
          }
          variant(name: $graphVariant) {
            id
            sourceVariant {
              id
              activeSchemaPublish {
                id
                compositionResult {
                  graphCompositionID
                  ...SubgraphMetaSubgraphsFragment
                }
              }
            }
            usageLastReportedAt
            activeSchemaPublish {
              id
              compositionResult {
                graphCompositionID
                ...SubgraphMetaSubgraphsFragment
              }
            }
            permissions {
              canQuerySchemas
            }
            entities {
              ...SubgraphMetaEntitiesFragment
            }
          }
        }
      }
      ${subgraphMetaSubgraphsFragment}
      ${subgraphMetaEntitiesFragment}
    `,
    {
      variables: { graphId, graphVariant },
      skip: !graphId || !graphVariant,
    },
  );

  const isObjectField = coordinateKind === CoordinateKind.OBJECT_FIELD;

  const {
    data: boundsData,
    loading: boundsLoading,
    error: boundsError,
  } = useQuery<
    GraphQLTypes.CoordinateLandingPageBoundsQuery,
    GraphQLTypes.CoordinateLandingPageBoundsQueryVariables
  >(
    gql`
      query CoordinateLandingPageBoundsQuery(
        $graphId: ID!
        $graphVariant: String!
        $parentType: String!
        $fieldName: String!
        $coordinateKind: CoordinateKind!
        $isObjectField: Boolean!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            usageLastReportedAt
            fieldInsights(fieldName: $fieldName, parentType: $parentType)
              @include(if: $isObjectField) {
              firstSeen
              lastSeen
              earliestRetentionTime
            }
            coordinateInsights(
              coordinateKind: $coordinateKind
              namedAttribute: $fieldName
              namedType: $parentType
            ) @skip(if: $isObjectField) {
              firstSeen
              lastSeen
              earliestRetentionTime
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId,
        graphVariant,
        parentType: typeName,
        fieldName,
        coordinateKind,
        isObjectField,
      },
      skip: !graphId || !graphVariant || !typeName || !fieldName,
    },
  );

  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useQuery<
    GraphQLTypes.CoordinateLandingPageMetricsQuery,
    GraphQLTypes.CoordinateLandingPageMetricsQueryVariables
  >(
    gql`
      query CoordinateLandingPageMetricsQuery(
        $graphId: ID!
        $timeFrom: Timestamp!
        $timeTo: Timestamp!
        $filterFieldUsage: ServiceFieldUsageFilter
        $filterFieldExecutions: ServiceFieldExecutionsFilter
        $filterCoordinateUsage: ServiceCoordinateUsageFilter
        $resolution: Resolution
        $isObjectField: Boolean!
      ) {
        service(id: $graphId) {
          id
          stats: statsWindow(from: $timeFrom, to: $timeTo) {
            fieldUsageByContextAndQuery: fieldUsage(filter: $filterFieldUsage)
              @include(if: $isObjectField) {
              ...ServiceFieldUsageRecordFragment
            }
            coordinateUsageByContextAndQuery: coordinateUsage(
              filter: $filterCoordinateUsage
            ) @skip(if: $isObjectField) {
              ...ServiceCoordinateUsageRecordFragment
            }
          }
          # This section is separate than "stats" because the resolution inflicts
          # a performance impact on the server.
          allStats: statsWindow(
            from: $timeFrom
            to: $timeTo
            resolution: $resolution
          ) {
            fieldExecutions(filter: $filterFieldExecutions)
              @include(if: $isObjectField) {
              timestamp
              metrics {
                errorsCount
                observedExecutionCount
                estimatedExecutionCount
                referencingOperationCount
                fieldHistogram {
                  sparseBuckets
                  p50: durationMs(percentile: 0.5)
                  p90: durationMs(percentile: 0.9)
                  p95: durationMs(percentile: 0.95)
                }
              }
            }
            coordinateUsage(filter: $filterCoordinateUsage)
              @skip(if: $isObjectField) {
              timestamp
              metrics {
                referencingOperationCount
                estimatedExecutionCount
              }
            }
          }
        }
      }
      ${ServiceFieldUsageRecordFragment}
      ${ServiceCoordinateUsageRecordFragment}
    `,
    {
      variables: {
        graphId,
        filterFieldUsage: fieldUsageFilter,
        filterFieldExecutions: fieldExecutionsFilter,
        filterCoordinateUsage: coordinateUsageFilter,
        isObjectField,
        ...partialTimeRange,
      },
      skip: !graphId || !fieldName || !typeName,
    },
  );

  const accessBounds = useMemo<CurrentFieldAccessBounds>(() => {
    const { firstSeen, lastSeen, earliestRetentionTime } =
      (isObjectField
        ? boundsData?.service?.variant?.fieldInsights
        : boundsData?.service?.variant?.coordinateInsights) || {};
    const usageLastReportedAt =
      boundsData?.service?.variant?.usageLastReportedAt;

    const firstSeenDate = firstSeen ? new Date(firstSeen) : undefined;
    const lastSeenDate = lastSeen ? new Date(lastSeen) : undefined;
    const earliestRetentionTimeDate = earliestRetentionTime
      ? new Date(earliestRetentionTime)
      : undefined;
    const variantUsageLastReportedAt = usageLastReportedAt
      ? new Date(usageLastReportedAt)
      : undefined;
    return {
      firstSeen: firstSeenDate,
      lastSeen: lastSeenDate,
      earliestRetentionTime: earliestRetentionTimeDate,
      variantUsageLastReportedAt,
    };
  }, [
    boundsData?.service?.variant?.coordinateInsights,
    boundsData?.service?.variant?.fieldInsights,
    boundsData?.service?.variant?.usageLastReportedAt,
    isObjectField,
  ]);

  const schema = useMemo(() => {
    const schemaDocument = schemaData?.service?.schema?.document;
    if (schemaLoading || !schemaDocument) return null;

    try {
      return buildSchemaLeniently(schemaDocument).schema;
    } catch {
      return null;
    }
  }, [schemaData, schemaLoading]);

  const rootTypes = useMemo(
    () => (schema ? Object.values(getRootTypeNames(schema)) : []),
    [schema],
  );

  const getSubgraphs = useMemo(() => {
    let subgraphConfigs;

    if (schemaData?.service?.variant?.sourceVariant) {
      subgraphConfigs =
        schemaData?.service?.variant?.sourceVariant?.activeSchemaPublish
          ?.compositionResult?.subgraphConfigs;
    } else {
      subgraphConfigs =
        schemaData?.service?.variant?.activeSchemaPublish?.compositionResult
          ?.subgraphConfigs;
    }

    const entities =
      schemaData?.service?.variant?.entities?.__typename === 'EntitiesResponse'
        ? schemaData.service.variant.entities.entities
        : undefined;
    if (schemaLoading) return undefined;
    return (
      buildSubgraphMetaHelpers(entities, subgraphConfigs, schema)
        ?.getSubgraphs ?? undefined
    );
  }, [schemaData, schemaLoading, schema]);

  const fieldData = useMemo(() => {
    const parentType = schema?.getType(typeName);
    const field =
      parentType &&
      'getFields' in parentType &&
      parentType.getFields()[fieldName];

    const enumValue =
      parentType instanceof GraphQLEnumType && parentType.getValue(fieldName);

    if (
      field &&
      (isGraphqlField(field) || parentType instanceof GraphQLInputObjectType)
    ) {
      return {
        field,
        parentType,
        isRootType: rootTypes.includes(parentType?.name),
      };
    } else if (enumValue) {
      return {
        field: enumValue ?? undefined,
        parentType,
        isRootType: rootTypes.includes(parentType?.name),
      };
    } else {
      return {
        field: undefined,
        parentType: undefined,
        isRootType: false,
      };
    }
  }, [schema, typeName, fieldName, rootTypes]);

  const schemaInfo = useMemo<SchemaInfoWithoutUrl | null>(() => {
    if (!schemaData || !schemaData?.service?.schema || !schema) return null;
    const { document, createdAt, gitContext, fieldCount, typeCount } =
      schemaData.service.schema;
    const { documentNode } = tolerantParseSchema(
      monacoEditor.editor.createModel(document),
    );
    return {
      type: 'Graph',
      displayName: 'Supergraph schema',
      id: SUPERGRAPH_SCHEMA_ID,
      text: document,
      typeDefs: documentNode,
      lastUpdated: new Date(createdAt),
      version: gitContext?.commit ?? undefined,
      typeCount,
      fieldCount,
      language: 'graphql',
    };
  }, [schema, schemaData]);

  const fieldMetrics = useMemo<FieldMetrics>(() => {
    const usageMetricsByContextAndQuery =
      (isObjectField
        ? metricsData?.service?.stats?.fieldUsageByContextAndQuery
        : metricsData?.service?.stats?.coordinateUsageByContextAndQuery) ?? [];
    const fieldRequests = (
      (isObjectField
        ? metricsData?.service?.allStats?.fieldExecutions
        : metricsData?.service?.allStats?.coordinateUsage) ?? []
    ).map<FieldRequest>((usage) => ({
      timestamp: usage.timestamp,
      requestCount: usage.metrics?.referencingOperationCount ?? 0,
      estimatedExecutionCount: usage.metrics?.estimatedExecutionCount ?? 0,
    }));
    const fieldExecutions = metricsData?.service?.allStats?.fieldExecutions;
    const errors: FieldError[] =
      fieldExecutions?.map(({ metrics, timestamp }) => ({
        timestamp,
        errorCount: metrics.errorsCount,
        percentage:
          metrics.errorsCount === 0 || metrics.estimatedExecutionCount === 0
            ? 0
            : metrics.errorsCount / metrics.estimatedExecutionCount,
      })) ?? [];

    const { totalOperations, clientMetrics } = buildClientMetrics(
      usageMetricsByContextAndQuery,
    );

    return {
      clientMetrics,
      totalClients: clientMetrics.size,
      totalOperations,
      requests: fieldRequests,
      latencyDistribution:
        metricsData?.service?.allStats?.fieldExecutions ?? [],
      errors,
    };
  }, [
    isObjectField,
    metricsData?.service?.allStats?.coordinateUsage,
    metricsData?.service?.allStats?.fieldExecutions,
    metricsData?.service?.stats?.coordinateUsageByContextAndQuery,
    metricsData?.service?.stats?.fieldUsageByContextAndQuery,
  ]);

  const error = metricsError || schemaError || boundsError;

  const emptyState = useMemo<CurrentFieldEmptyState>(() => {
    const variantHasUsage = Boolean(accessBounds.variantUsageLastReportedAt);
    const fieldHasUsage = Boolean(accessBounds.lastSeen);

    const { fromDate, toDate } = partialTimeRange;
    const fieldUsageOutsideTimeBounds =
      fieldHasUsage &&
      (moment(accessBounds.lastSeen).isBefore(fromDate) ||
        moment(accessBounds.firstSeen).isAfter(toDate));

    const fieldEarliestRetentionInDays = accessBounds.earliestRetentionTime
      ? Math.round(
          moment
            .duration(moment().diff(accessBounds?.earliestRetentionTime))
            .asDays(),
        )
      : undefined;

    const showEmptyState =
      !boundsLoading &&
      (!variantHasUsage || !fieldHasUsage || fieldUsageOutsideTimeBounds);

    return {
      showEmptyState,
      variantHasUsage,
      fieldHasUsage,
      fieldUsageOutsideTimeBounds,
      fieldEarliestRetentionInDays,
      fieldLastSeen: accessBounds.lastSeen,
    };
  }, [
    accessBounds.earliestRetentionTime,
    accessBounds.firstSeen,
    accessBounds.lastSeen,
    accessBounds.variantUsageLastReportedAt,
    boundsLoading,
    partialTimeRange,
  ]);

  return error
    ? { error }
    : !graphId || !graphVariant || !fieldName || !typeName
      ? {
          error: new Error(
            `All variables are required "graphId", "variant", "fieldName", and "typeName".`,
          ),
        }
      : {
          summaryLoading: schemaLoading,
          metricsLoading,
          boundsLoading,
          graphId,
          variant: graphVariant,
          fieldName,
          typeName,
          schemaInfo,
          fieldMetrics,
          canViewClientVersions: currentPlan?.clientVersions ?? false,
          hasActiveClientFilters:
            hasActiveFiltersForSubset(clientFilterDefaults),
          timeRange: {
            ...partialTimeRange,
            ranges: (currentPlan?.ranges as PossibleRanges) ?? [],
            maxRangeInDays: currentPlan?.maxRangeInDays ?? 30,
          },
          getSubgraphs,
          accessBounds,
          ...fieldData,
          emptyState,
        };
};

export const buildClientMetrics = (
  usageMetricsByContextAndQuery:
    | Array<GraphQLTypes.ServiceFieldUsageRecordFragment>
    | Array<GraphQLTypes.ServiceCoordinateUsageRecordFragment>,
): {
  totalOperations: number;
  clientMetrics: Map<ClientName, ClientMetrics>;
} => {
  let clientMetrics = new Map<ClientName, ClientMetrics>();
  const allOperations = new Set<string>();

  usageMetricsByContextAndQuery.forEach(({ groupBy, metrics }) => {
    const hasExecutions = metrics.__typename === 'ServiceFieldUsageMetrics';
    const clientName = groupBy?.clientName || '';

    if (!clientMetrics.has(clientName)) {
      clientMetrics.set(clientName, {
        requests: 0,
        executions: 0,
        operationMetrics: new Map(),
      });
    }
    const curClientMetrics = clientMetrics.get(clientName) as ClientMetrics;

    curClientMetrics.requests += metrics.referencingOperationCount;
    curClientMetrics.executions += hasExecutions ? metrics.executionCount : 0;

    const queryName = groupBy.queryName || '';
    const queryId = groupBy.queryId || '';
    allOperations.add(queryId);

    if (!curClientMetrics.operationMetrics.has(queryId)) {
      curClientMetrics.operationMetrics.set(queryId, {
        allVersions: new Set(),
        allOperationTypes: new Set(),
        versionCount: 0,
        executions: 0,
        estimatedExecutions: 0,
        requests: 0,
        queryId,
        queryName,
      });
    }
    const curOperationMetrics = curClientMetrics.operationMetrics.get(
      queryId,
    ) as OperationMetrics;

    curOperationMetrics.allVersions.add(groupBy?.clientVersion || '');
    curOperationMetrics.allOperationTypes.add(groupBy?.operationType || '');
    curOperationMetrics.versionCount = curOperationMetrics.allVersions.size;
    curOperationMetrics.executions += hasExecutions
      ? metrics.executionCount
      : 0;
    curOperationMetrics.estimatedExecutions += hasExecutions
      ? metrics.estimatedExecutionCount
      : 0;
    curOperationMetrics.requests += metrics.referencingOperationCount;
  });

  if (clientMetrics.size > 1) {
    // Sort the client metrics by requests.
    clientMetrics = new Map(
      [...clientMetrics.entries()]
        .sort(([_, a], [__, b]) => b.requests - a.requests)
        .sort(([_, a], [__, b]) => b.executions - a.executions),
    );
  }

  return { totalOperations: allOperations.size, clientMetrics };
};
